/**
 * /checkin/plans — mobile-first membership + Talk Credit pack picker.
 *
 * Where users land when they choose "Continue with Dr. C" after their
 * Complimentary Check-In, or when they tap "Add Talk Credits" on Home.
 * Reads from the public catalog + posts to /api/checkin/checkout/{pack,membership}.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ChevronLeft, Sparkles, Wallet } from "lucide-react";
import { CheckInShell, CheckInLogo, PrimaryButton, GhostButton, CHECKIN_PALETTE } from "./_shell";
import { checkinApi, getToken } from "@/lib/checkinApi";

function money(cents) {
  return `$${(Math.round(cents) / 100).toFixed(0)}`;
}

export default function CheckInPlans() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [err, setErr] = useState("");
  const [creating, setCreating] = useState("");

  useEffect(() => {
    if (!getToken()) {
      navigate("/checkin/signin", { replace: true });
      return;
    }
    (async () => {
      try {
        const c = await checkinApi.catalog();
        setCatalog(c);
      } catch (e) {
        setErr(e.message || "Couldn't load plans.");
      }
    })();
  }, [navigate]);

  async function buyMembership(slug) {
    setErr(""); setCreating(`m:${slug}`);
    try {
      const out = await checkinApi.request
        ? await checkinApi.request("/checkout/membership", { method: "POST", body: { slug }, auth: true })
        : await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkin/checkout/membership`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
            body: JSON.stringify({ slug }),
          }).then((r) => r.json().then((j) => (r.ok ? j : Promise.reject(new Error(j?.detail?.message || j?.detail?.code || "Checkout failed")))));
      window.location.href = out.checkout_url;
    } catch (e) {
      setErr(e.message || "Checkout failed.");
    } finally {
      setCreating("");
    }
  }

  async function buyPack(slug) {
    setErr(""); setCreating(`p:${slug}`);
    try {
      const t = getToken();
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkin/checkout/pack`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
        body: JSON.stringify({ slug }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail?.message || j?.detail?.code || "Checkout failed");
      window.location.href = j.checkout_url;
    } catch (e) {
      setErr(e.message || "Checkout failed.");
    } finally {
      setCreating("");
    }
  }

  if (!catalog && !err) {
    return (
      <CheckInShell testid="checkin-plans-loading">
        <div className="mt-24 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: CHECKIN_PALETTE.purple }} />
        </div>
      </CheckInShell>
    );
  }

  return (
    <CheckInShell testid="checkin-plans">
      <div className="pt-2 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-xs"
                style={{ color: CHECKIN_PALETTE.purple }} data-testid="plans-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </button>
        <CheckInLogo small />
        <div className="w-10" />
      </div>

      <div className="mt-6">
        <div className="text-[10px] font-semibold uppercase tracking-[0.28em]"
             style={{ color: CHECKIN_PALETTE.purple }}>Continue with Dr. C</div>
        <h1 className="mt-2 font-serif text-3xl leading-tight"
            style={{ color: CHECKIN_PALETTE.ink, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          Choose how you&rsquo;d like to keep checking in.
        </h1>
        <p className="mt-3 text-[13.5px] leading-relaxed" style={{ color: "#5C5468" }}>
          A membership includes a monthly allocation of Talk Credits. Prefer to add credits without
          a membership? You can pick a pack below.
        </p>
        <p className="mt-2 text-[12.5px] leading-relaxed" style={{ color: "#5C5468" }}
           data-testid="plans-credit-explainer">
          One Talk Credit includes up to five message-and-response exchanges with Dr.&nbsp;C. Longer check-ins may use additional credits.
        </p>
      </div>

      {err ? (
        <div className="mt-4 rounded-2xl border px-4 py-3 text-sm"
             style={{ borderColor: CHECKIN_PALETTE.danger, color: CHECKIN_PALETTE.danger, background: "#FDF1F3" }}
             data-testid="plans-error">
          {err}
        </div>
      ) : null}

      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold mb-3"
             style={{ color: CHECKIN_PALETTE.purple }}>
          Memberships
        </div>
        <div className="space-y-3">
          {(catalog?.memberships || []).map((m) => (
            <div
              key={m.slug}
              className="rounded-3xl p-5"
              style={{
                background: m.featured
                  ? `linear-gradient(135deg, ${CHECKIN_PALETTE.purple}, #3A1362)`
                  : "white",
                color: m.featured ? "white" : CHECKIN_PALETTE.ink,
                border: m.featured ? "none" : `1px solid ${CHECKIN_PALETTE.silverSoft}`,
                boxShadow: m.featured ? "0 20px 40px -22px rgba(92,30,138,0.55)" : "none",
              }}
              data-testid={`plan-membership-${m.slug}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-80">
                    {m.featured ? "Most chosen" : "Membership"}
                  </div>
                  <div className="mt-1 font-serif text-2xl"
                       style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                    {m.name}
                  </div>
                  <div className="mt-1 text-[13px] opacity-80"
                       data-testid={`plan-credits-${m.slug}`}>
                    {m.monthly_credits} Talk Credits — up to {m.monthly_credits * 5} exchanges monthly
                  </div>
                </div>
                <div className="font-serif text-3xl"
                     style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                  {money(m.monthly_price_cents)}
                </div>
              </div>
              <p className="mt-3 text-[13px] leading-relaxed opacity-90">
                {m.description}
              </p>
              <button
                type="button"
                onClick={() => buyMembership(m.slug)}
                disabled={creating === `m:${m.slug}`}
                data-testid={`plan-membership-buy-${m.slug}`}
                className="mt-4 w-full rounded-full py-3 font-medium text-[14px]"
                style={{
                  background: m.featured ? "white" : CHECKIN_PALETTE.purple,
                  color: m.featured ? CHECKIN_PALETTE.purple : "white",
                }}
              >
                {creating === `m:${m.slug}`
                  ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Preparing checkout…</span>
                  : "Choose this membership"}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold mb-3"
             style={{ color: CHECKIN_PALETTE.purple }}>
          Talk Credit packs
        </div>
        <div className="space-y-3">
          {(catalog?.credit_packs || []).map((p) => (
            <div key={p.slug}
                 className="rounded-2xl bg-white p-4 flex items-center justify-between"
                 style={{ border: `1px solid ${CHECKIN_PALETTE.silverSoft}` }}
                 data-testid={`plan-pack-${p.slug}`}>
              <div>
                <div className="flex items-center gap-2">
                  <Wallet className="w-4 h-4" style={{ color: CHECKIN_PALETTE.purple }} />
                  <div className="font-medium text-[14.5px]">{p.name}</div>
                </div>
                {p.bonus_credits ? (
                  <div className="text-[11px] mt-0.5" style={{ color: CHECKIN_PALETTE.green }}>
                    +{p.bonus_credits} bonus credits
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-3">
                <div className="font-serif text-lg"
                     style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: CHECKIN_PALETTE.ink }}>
                  {money(p.price_cents)}
                </div>
                <button
                  type="button"
                  onClick={() => buyPack(p.slug)}
                  disabled={creating === `p:${p.slug}`}
                  data-testid={`plan-pack-buy-${p.slug}`}
                  className="rounded-full px-4 py-2 text-[13px] font-medium text-white"
                  style={{ background: CHECKIN_PALETTE.purple }}
                >
                  {creating === `p:${p.slug}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Add"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 rounded-2xl border p-4 flex items-start gap-3"
           style={{ borderColor: CHECKIN_PALETTE.silverSoft, background: "white" }}>
        <Sparkles className="w-4 h-4 mt-0.5" style={{ color: CHECKIN_PALETTE.purple }} />
        <p className="text-[12.5px] leading-relaxed" style={{ color: "#4A4256" }}>
          Secure checkout via Square. Eligible buyers can split payments with
          Afterpay/Clearpay at checkout.
        </p>
      </div>

      <div className="mt-6">
        <Link to="/checkin/home"><GhostButton testid="plans-home">Back to home</GhostButton></Link>
      </div>
    </CheckInShell>
  );
}
